import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { RawWorklogEntry } from '../../stores/workloadStore';
import type { ScheduleItem } from '../../stores/scheduleStore';
import { useUsersStore } from '../../stores/usersStore';
import { formatHoursMinutes } from '../../utils/timesheet-capacity';
import mondaySdk from 'monday-sdk-js';

const monday = mondaySdk();

interface ItemDetails {
  itemName: string;
  boardName: string;
  isSubitem: boolean;
  parentName?: string;
}

interface WorklogDetailPopupProps {
  worklog: RawWorklogEntry;
  position: { x: number; y: number };
  /** Local schedule items — used to resolve names without API call */
  localItems: ScheduleItem[];
  canDelete: boolean;
  onDelete: () => void;
  onClose: () => void;
}

const WorklogDetailPopup: React.FC<WorklogDetailPopupProps> = ({
  worklog,
  position,
  localItems,
  canDelete,
  onDelete,
  onClose,
}) => {
  const [details, setDetails] = useState<ItemDetails | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Try local items first
    const localItem = localItems.find((i) => String(i.mondayItemId) === worklog.mondayItemId);
    if (localItem) {
      setDetails({
        itemName: localItem.name,
        boardName: localItem.boardName ?? '',
        isSubitem: localItem.isSubitem ?? false,
        parentName: localItem.parentItemName,
      });
      return;
    }

    // Fetch from monday.com API
    let cancelled = false;
    setLoading(true);

    monday.api(
      `query ($ids: [ID!]!) {
        items(ids: $ids) {
          name
          board { name }
          parent_item { name }
        }
      }`,
      { variables: { ids: [worklog.mondayItemId] } },
    ).then((res) => {
      if (cancelled) return;
      const item = (res as { data?: { items?: Array<{ name: string; board?: { name: string }; parent_item?: { name: string } }> } }).data?.items?.[0];
      if (item) {
        setDetails({
          itemName: item.name,
          boardName: item.board?.name ?? '',
          isSubitem: !!item.parent_item,
          parentName: item.parent_item?.name,
        });
      }
    }).catch(() => {
      // Silently fail — popup still shows hours + date
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });

    return () => { cancelled = true; };
  }, [worklog.mondayItemId, localItems]);

  const popupRef = useRef<HTMLDivElement>(null);
  const [adjustedPos, setAdjustedPos] = useState(position);

  useLayoutEffect(() => {
    const el = popupRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let { x, y } = position;
    // Center horizontally, then clamp to viewport
    const halfW = rect.width / 2;
    if (x - halfW < 8) x = halfW + 8;
    if (x + halfW > vw - 8) x = vw - halfW - 8;
    // Clamp vertically
    if (y + rect.height > vh - 8) y = Math.max(8, y - rect.height - 8);
    setAdjustedPos({ x, y });
  }, [position]);

  return (
    <div
      ref={popupRef}
      className="rp-worklog-popup"
      style={{
        position: 'fixed',
        top: adjustedPos.y,
        left: adjustedPos.x,
        transform: 'translateX(-50%)',
        zIndex: 1000,
      }}
      onMouseLeave={onClose}
    >
      <div className="rp-worklog-popup__header">
        <strong>{formatHoursMinutes(worklog.hours)}</strong>
        {details && <span> &mdash; {details.itemName}</span>}
        {loading && <span className="rp-worklog-popup__loading"> Loading...</span>}
      </div>

      {details && (details.boardName || details.parentName) && (
        <div className="rp-worklog-popup__meta">
          {details.boardName && (
            <span className="rp-worklog-popup__board">{details.boardName}</span>
          )}
          {details.parentName && (
            <>
              {details.boardName && <span className="rp-worklog-popup__sep"> / </span>}
              <span className="rp-worklog-popup__parent">{details.parentName}</span>
            </>
          )}
        </div>
      )}

      <div className="rp-worklog-popup__date">{worklog.date}</div>

      {worklog.createdByUserId && worklog.createdByUserId !== worklog.mondayUserId && (
        <div className="rp-worklog-popup__meta" style={{ fontSize: '11px', color: '#676879' }}>
          Created by: {useUsersStore.getState().users.find((u) => String(u.id) === worklog.createdByUserId)?.name ?? `User #${worklog.createdByUserId}`}
        </div>
      )}

      {worklog.description && (
        <div className="rp-worklog-popup__desc">{worklog.description}</div>
      )}

      {canDelete && (
        <div className="rp-worklog-popup__actions">
          <button
            className="rp-worklog-popup__btn rp-worklog-popup__btn--delete"
            onClick={() => {
              if (window.confirm('Are you sure you want to delete this worklog?')) {
                onDelete();
              }
            }}
          >
            Delete
          </button>
        </div>
      )}
      {!canDelete && (
        <div className="rp-worklog-popup__locked">View only</div>
      )}
    </div>
  );
};

export default WorklogDetailPopup;
